
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import { CommentCreateInterface, ProfileMapInterface } from '@/services/QC-V2/chat/interfaces';
import { CommentRate, CommentRepositoryInterface } from '@/services/QC-V2/comment/interfaces';
import {
  ChatFeedbackInterface,
  FeedbackRepositoryInterface,
  FeedbackStatus,
  TicketFeedbackInterface,
} from '@/services/QC-V2/feedback/interfaces';
import { Roles } from '@/services/Roles';
import DeleteCommentDialog from '@/components/dialogs/qc-v2/comment/DeleteCommentDialog.vue';

@Component({
  mixins: [
    Themeable,
  ],
  components: {
    DeleteCommentDialog,
  },
  inject: [
    'commentRepositoryV2',
    'feedbackRepositoryV2',
  ],
})
export default class TicketFeedbackComments extends Vue {
  @Prop() private feedback!: TicketFeedbackInterface;
  @Prop() private profilesMap!: ProfileMapInterface;

  private loading: boolean = true;

  private commentRepositoryV2!: CommentRepositoryInterface;
  private feedbackRepositoryV2!: FeedbackRepositoryInterface;

  private commentRates: typeof CommentRate = CommentRate;
  private commentRate: CommentRate = CommentRate.NOT_RATED;

  private commentText: string = '';

  private commentIdForEdit: number = 0;
  private editCommentText: string = '';

  private collapsed: boolean = false;

  private userRoles: object = Roles;

  private deleteCommentDialogVisible: boolean = false;
  private deleteCommentDialogEvent!: string;
  private deleteCommentDialogData!: { ticketId?: number, commentId?: number, index?: number, feedbackId?: number };

  private async created(): Promise<void> {
    this.loading = false;
  }

  private userHasScopes(scope: string): boolean {
    return this.$store.getters.scopes.includes(scope);
  }

  private async createFeedbackComment(): Promise<void> {
    this.commentText = this.commentText.trim();
    if (!this.commentText) {
      return;
    }

    const commentData: CommentCreateInterface = {
      author: this.$store.getters.id,
      text: this.commentText,
      rate: this.commentRate,
      timestamp: new Date().toISOString(),
    };

    try {
      const comment = await this.commentRepositoryV2.createTicketFeedbackComment(this.feedback.id, commentData);
      await this.changeFeedbackStatus();
      this.$successDialog.show('Comment Created Successfully!');
      this.feedback.comments.push(comment);
      this.commentText = '';
      this.commentRate = CommentRate.NOT_RATED;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private showDeleteFeedbackCommentDialog(feedbackId: number, commentId: number): void {
    this.deleteCommentDialogData = { feedbackId, commentId };
    this.deleteCommentDialogEvent = 'feedback-comment-deleted';
    this.deleteCommentDialogVisible = true;
  }

  private async deleteComment(data: { feedbackId: number, commentId: number }): Promise<void> {
    try {
      await this.commentRepositoryV2.deleteTicketFeedbackComment(data.feedbackId, data.commentId);
      this.$successDialog.show('Comment Deleted Successfully!');
      this.feedback.comments = this.feedback.comments.filter((comment) => comment.id !== data.commentId);
      this.deleteCommentDialogVisible = false;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private showEditBox(commentId: number, commentText: string): void {
    if (this.commentIdForEdit === commentId) {
      this.editCommentText = '';
      this.commentIdForEdit = 0;
      return;
    }

    this.editCommentText = commentText;
    this.commentIdForEdit = commentId;
  }

  private async editComment(feedbackId: number, commentId: number, commentIndex: number): Promise<void> {
    try {
      const commentData: CommentCreateInterface = {
        author: this.$store.getters.id,
        text: this.editCommentText,
        rate: this.commentRate,
        timestamp: new Date().toISOString(),
      };

      await this.commentRepositoryV2.updateTicketFeedbackComment(feedbackId, commentId, commentData);
      this.$successDialog.show('Comment Edited Successfully!');
      this.feedback.comments[commentIndex].text = this.editCommentText;

      this.commentIdForEdit = 0;
      this.editCommentText = '';
      this.commentRate = CommentRate.NOT_RATED;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private async changeFeedbackStatus(): Promise<void> {
    const viewer = this.$store.getters.id;
    if (this.feedback.feedbackTo === viewer && this.feedback.status === FeedbackStatus.SEEN) {
      await this.feedbackRepositoryV2.changeChatFeedbackStatus(this.feedback.id, FeedbackStatus.REPLIED);
      this.feedback.status = FeedbackStatus.REPLIED;
      return;
    }

    if (this.feedback.feedbackFrom === viewer && this.feedback.status === FeedbackStatus.REPLIED) {
      await this.feedbackRepositoryV2.changeChatFeedbackStatus(this.feedback.id, FeedbackStatus.ANSWERED);
      this.feedback.status = FeedbackStatus.ANSWERED;
      return;
    }
  }

  private setCommentRate(rate: CommentRate): void {
    if (rate === this.commentRate) {
      this.commentRate = CommentRate.NOT_RATED;
      return;
    }

    this.commentRate = rate;
  }
}
